// Drawing a saved or listed PHOTO without ever showing a broken-image icon (busy-my-web#691).
//
// Saffet: "The photo is not visible in a research pack". Since the photo-row change (98e9fc94,
// 2026-10-27) the search row draws each picture from the search engine's own small THUMBNAIL and
// opens the full picture only on click — and the save kept only the full picture's URL. Many sites
// refuse to serve their images to other sites (hotlink protection), so the very photo that showed
// in the search came back as a broken frame in the pack. Two halves to the fix:
//
//   • the save keeps the thumbnail. A research item has no thumbnail field (Url, Content, Title,
//     Kind, Query), and a photo's Content was always empty, so a photo's thumbnail is stored in
//     `content` and `url` stays the full picture (lib/store/modules/ongoing.js `researchSaveItem`).
//   • every surface that draws one walks the same list of candidates here: a TILE tries the
//     thumbnail first, then the full picture; an OPENED picture tries the full picture first, then
//     the thumbnail. When the last candidate fails too there is nothing to draw, and the caller
//     hides the tile (or, on a row that carries a name and controls, shows the photo glyph) — a
//     broken-image icon is never the answer. Photos saved before this fix have only the full URL:
//     one candidate, so a blocked one simply hides.
//
// Kept free of Vue so the rule is unit-testable on its own; components hold the per-item state in
// a reactive object via composables/usePhotoFallback.js (or `data()` for an Options API form).

const WEB = /^https?:\/\//i
// A pasted / dropped picture is held inline (research.js `addLocalItem`): a data: or blob: URL.
const INLINE = /^(data:image\/|blob:)/i
// A non-photo item draws its URL only when that URL is itself an image file.
const IMAGE_FILE = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|#|$)/i

const str = (v) => (typeof v === 'string' ? v.trim() : '')

/**
 * The item's small picture: `thumbnail` (every result kind's field, and a search photo's since
 * #691), the photo row's older `thumb`, or — for a SAVED photo — `content` when it is a web
 * address, which is where the save keeps it.
 *
 * @param {object} item a photo / research item
 * @returns {string} the thumbnail URL, or ''
 */
export function photoThumbnail(item) {
  if (!item) return ''
  const own = str(item.thumbnail) || str(item.thumb)
  if (own) return own
  const content = str(item.content)
  return item.kind === 'photo' && WEB.test(content) ? content : ''
}

/**
 * Every URL worth trying for `item`, best first, without repeats.
 *
 * A photo: thumbnail → full picture → an inline picture held in `content`; with `open` the full
 * picture leads (the viewer is where the big one is wanted) and the thumbnail is its stand-in.
 * Anything else (a video's poster, a card's picture): its thumbnail, then its URL only when that
 * URL is an image file — a video's or a page's address is not a picture.
 *
 * @param {object} item
 * @param {{ open?: boolean }} [opts]
 * @returns {string[]}
 */
export function photoSources(item, { open = false } = {}) {
  if (!item) return []
  const thumb = photoThumbnail(item)
  const url = str(item.url)
  if (item.kind !== 'photo') return [...new Set([thumb, IMAGE_FILE.test(url) ? url : ''].filter(Boolean))]
  const content = str(item.content)
  const inline = INLINE.test(content) ? content : ''
  const order = open ? [url, thumb, inline] : [thumb, url, inline]
  return [...new Set(order.filter(Boolean))]
}

/**
 * The candidate to try after `failed` broke, or '' when it was the last (or not one of them).
 *
 * @param {object} item
 * @param {string} failed the src that just failed to load
 * @param {{ open?: boolean }} [opts]
 * @returns {string}
 */
export function nextPhotoSource(item, failed, opts) {
  const list = photoSources(item, opts)
  const at = list.indexOf(str(failed))
  return at === -1 ? '' : (list[at + 1] || '')
}

/**
 * Per-item fallback state over a store object the caller owns (a `reactive({})` in a component, so
 * the template re-renders when a candidate fails).
 *
 *   src(item)              — the candidate to draw now ('' once every one has failed)
 *   hidden(item)           — nothing left to draw: hide the tile / show the glyph
 *   onError(item, failed?) — the drawn candidate broke; move to the next. When the failed src is
 *                            given and is no longer the current one (a late second error event),
 *                            it is ignored, so one failure can never skip a candidate.
 *   reset(item)            — try it from the first candidate again
 *
 * Items are keyed by id (url / content for an id-less one), so the same photo in two lists of one
 * component shares its state — it failed once, it failed.
 *
 * @param {object} failed the store: key → how many candidates have failed
 * @param {{ open?: boolean }} [opts]
 */
export function createPhotoFallback(failed = {}, opts = {}) {
  const keyOf = (item) => String(item?.id ?? item?.url ?? item?.content ?? '')
  const src = (item) => photoSources(item, opts)[failed[keyOf(item)] || 0] || ''
  return {
    src,
    hidden: (item) => !src(item),
    onError(item, failedSrc) {
      if (!item) return
      if (typeof failedSrc === 'string' && failedSrc && failedSrc !== src(item)) return
      const k = keyOf(item)
      failed[k] = (failed[k] || 0) + 1
    },
    // Start `item` over from its best candidate — the viewer's Refresh asks for exactly that.
    reset(item) {
      if (item) delete failed[keyOf(item)]
    },
  }
}

/**
 * The research DOCUMENT's pictures (the pack's NotionEditor). The document is the user's own HTML
 * and keeps a photo's FULL URL as its src — that URL is how lib/packDocUpdate and lib/packDocAnchor
 * recognise the photo in it, so it is never rewritten. This runs on an <img> that failed inside
 * it: the matching saved photo's next candidate (its thumbnail) is swapped in on the element only,
 * or, with nothing left, the picture is hidden. ProseMirror ignores attribute changes on a leaf
 * node's DOM, so neither touches the saved document.
 *
 * @param {Element} img the <img> that fired `error`
 * @param {object[]} items the pack's saved photos
 * @returns {'swapped'|'hidden'|null} what was done (null: not an <img>)
 */
export function docPhotoFallback(img, items) {
  if (!img || String(img.tagName).toUpperCase() !== 'IMG') return null
  const src = img.getAttribute('src') || ''
  const item = (items || []).find(r => r && photoSources(r, { open: true }).includes(src))
  const next = item ? nextPhotoSource(item, src, { open: true }) : ''
  if (next) {
    img.setAttribute('src', next)
    return 'swapped'
  }
  img.style.display = 'none'
  return 'hidden'
}

/**
 * Is `src` one of `item`'s candidates? The document's anchor search uses it so a picture whose src
 * was swapped to the thumbnail above is still found as that photo.
 */
export const isPhotoSource = (item, src) => !!src && photoSources(item, { open: true }).includes(String(src))
